// @ts-nocheck
import * as React from 'react';
import Icon from '../../components/common/Icon';
import { PageHero, Section, Ring, Sparkline, SendBtn, PresentBtn, SentBadge } from '../shared/InternalShared';
import { useEffect, useState } from 'react';
import { fetchStudentInfoItems } from '../services/internalContentApi';

// studentinfo.jsx — AI Powered Student Information / 360°. Accent: blue.
// "Everything about Aarav. Understood by AI."

export default function PageStudentInfo({ student, accent, sendToParent, onContentSelect }) {
  const first = student.child.split(" ")[0];
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [preview, setPreview] = useState(null);
  const [contentState, setContentState] = useState({ loading: true, error: '' });
  const selectedContent = items[selectedIndex] || items[0] || null;
  useEffect(() => { onContentSelect?.(selectedContent); }, [selectedContent, onContentSelect]);

  useEffect(() => {
    let current = true;
    setContentState({ loading: true, error: '' });
    setSelectedIndex(0);
    setPreview(null);
    fetchStudentInfoItems({ name: student.child, age: student.childAge, gender: student.gender })
      .then((data) => { if (current) setItems(data); })
      .catch((error) => { if (current) { setItems([]); setContentState({ loading: false, error: error?.message || 'Unable to load AI student information.' }); } })
      .finally(() => { if (current) setContentState((state) => ({ ...state, loading: false })); });
    return () => { current = false; };
  }, [student.child, student.childAge, student.gender]);

  const heroRight = (
    <div className="ip-card ip-rise" style={{ padding: 22, display: "grid", gridTemplateColumns: "auto 1fr", gap: 22, alignItems: "center", background: "var(--bg)" }}>
      <Ring value={84} size={132} stroke={10} valSize={34} cap="Exam ready" />
      <div style={{ display: "grid", gap: 14 }}>
        {[
          { l: "Attendance", v: "92%", trend: "+3 vs class", good: true },
          { l: "Homework streak", v: "18 days", trend: "Consistent", good: true },
          { l: "Focus index", v: "Watch", trend: "Dipped in maths", good: false },
        ].map((k) => (
          <div key={k.l} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontSize: 12, color: "var(--fg-tertiary)", fontWeight: 600 }}>{k.l}</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: "var(--fg)", fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}>{k.v}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: k.good ? "var(--green)" : "var(--amber)" }}>{k.trend}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="ip" style={{ "--accent": accent }}>
      <PageHero
        eyebrow="AI Student Information · 360°"
        icon="student"
        title={<>Everything about {first}.<br/><span className="ip-accent">Understood by AI.</span></>}
        lead="Behaviour, academics and discipline — combined into one living picture, aligned to the NEP 2020 Holistic Progress Report. Issues are caught before they reach the report card."
        actions={<>
          <SendBtn onClick={() => sendToParent("360° Holistic Progress Report", selectedContent)} label="Send 360° report" />
          <PresentBtn onClick={() => sendToParent("360° report walkthrough")} />
        </>}
        trust={{ label: "Aligned with", items: [
          { label: "Ministry of Education" },
          { label: "NEP 2020 HPR" },
        ] }}
        right={heroRight}
      />

      <Section
        eyebrow="360° intelligence"
        title="Six lenses on one child"
        hint="Each module turns daily activity into something a parent can act on tonight."
      >
        {contentState.loading ? <CardSkeletonGrid /> : null}
        {contentState.error ? <div className="ip-api-state" role="alert">{contentState.error}</div> : null}
        {!contentState.loading && !contentState.error && !items.length ? <div className="ip-api-state">No AI insights are available for this student yet.</div> : null}
        <div className="ip-grid ip-grid--3">
          {items.map((item, index) => (
            <ProgressCard key={`${item.MODULE}-${index}`} selected={selectedIndex === index}>
              <div className="ip-selectable-card" role="button" tabIndex={0} aria-pressed={selectedIndex === index} onClick={() => setSelectedIndex(index)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedIndex(index); } }}>
                <ModHead icon={moduleIcon(item.MODULE)} title={item.MODULE} isSent={item.IS_SENT} />
                <h3 className="ip-api-card__heading">{item.HEADING}</h3>
                <div className="ip-api-card__message">{item.MESSAGE}</div>
                {(item.AUDIO_URL || item.VIDEO_URL) ? <div className="ip-preview-actions">
                  {item.AUDIO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedIndex(index); setPreview({ type: 'audio', url: item.AUDIO_URL, title: item.HEADING }); }}>Audio preview</button> : null}
                  {item.VIDEO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedIndex(index); setPreview({ type: 'video', url: item.VIDEO_URL, title: item.HEADING }); }}>Video preview</button> : null}
                </div> : null}
              </div>
            </ProgressCard>
          ))}
        </div>
        {false && (
        <div className="ip-grid ip-grid--3">
          {/* Attendance */}
          <ProgressCard>
            <ModHead icon="calendar" title="Attendance Insights" />
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: 18 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="ip-stat__n" style={{ fontSize: 29 }}>82<sup>%</sup></div>
                  <span className="ip-tag" style={{ background: "var(--green-soft)", color: "var(--green)" }}>Above Average</span>
                </div>
                <div className="ip-stat__l">Last 30 days</div>
              </div>
              <Sparkline points={[70, 72, 72, 74, 82, 78, 87, 84]} width={160} height={52} />
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-secondary)", marginTop: 10, lineHeight: 1.5 }}>Above class average. No unusual absence pattern before exams.</div>
          </ProgressCard>

          {/* Homework */}
          <ProgressCard>
            <ModHead icon="check" title="Homework consistency" />
            <div className="ip-stat__n" style={{ fontSize: 29, marginTop: 18 }}>18-day streak</div>
            <div style={{ display: "flex", gap: 3, marginTop: 10 }}>
              {[...Array(16)].map((_, i) => (
                <span key={i} style={{ flex: 1, height: 20, borderRadius: 3, background: i < 2 ? "var(--bg-inset)" : "var(--accent)", opacity: i < 2 ? 1 : (0.2 + (i / 16) * 0.8) }} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-secondary)", marginTop: 10, lineHeight: 1.5 }}>Strong routine. Highest completion in language; dips on maths nights.</div>
          </ProgressCard>

          {/* Parent accountability */}
          <ProgressCard>
            <ModHead icon="parent" title="Parent accountability" />
            <div style={{ marginTop: 14, display: "grid", gap: 9 }}>
              {[
                { s: "Homework participation", v: 88, trend: "↑ 8.7%", color: "var(--accent)" },
                { s: "Leave management", v: 79, trend: "↑ 8.%", color: "var(--accent)" },
                { s: "Teacher remarks monitoring", v: 61, trend: "↑ 1%", color: "#ea580c" },
              ].map((row) => (
                <div key={row.s}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 4 }}>
                    <span style={{ color: "var(--fg)", fontWeight: 800, textTransform: "uppercase", fontSize: 10.5 }}>{row.s}</span>
                    <span style={{ color: "var(--fg)", fontWeight: 700 }}><em style={{ color: "var(--green)", fontStyle: "normal", fontWeight: 700, marginRight: 12 }}>{row.trend}</em>{row.v}%</span>
                  </div>
                  <div className="ip-bar"><i style={{ width: row.v + "%", background: row.color }} /></div>
                </div>
              ))}
            </div>
          </ProgressCard>

          {/* Early detection */}
          <ProgressCard>
            <ModHead icon="shield" title="Early performance detection" />
            <div style={{ marginTop: 30, padding: "12px 14px", borderRadius: 8, background: "rgba(180,83,9,0.08)", border: "1px solid rgba(180,83,9,0.18)", display: "inline-flex", gap: 14, alignItems: "center" }}>
              <strong style={{ color: "#9a3412", fontSize: 14 }}>Maths confidence dipping</strong>
              <span style={{ color: "#c2410c", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Trend: 2 weeks</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-secondary)", marginTop: 18, lineHeight: 1.5 }}>Flagged 3 weeks before the unit test — while there's still time to act.</div>
          </ProgressCard>

          {/* Diagnostic */}
          <ProgressCard>
            <ModHead icon="analytics" title="Academic diagnostic" />
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 52, lineHeight: 1.55 }}>
              Root cause isn't ability — it's a <strong style={{ color: "var(--fg)" }}>concept gap in fractions</strong> correlating with lower focus on maths-homework nights.
            </div>
          </ProgressCard>

          {/* HPR */}
          <ProgressCard selected>
            <ModHead icon="file" title="360° holistic progress report" />
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 18, lineHeight: 1.55, fontWeight: 600 }}>Academics, behaviour, discipline and strengths — one NEP 2020-aligned report, refreshed weekly.</div>
            <button className="ip-btn ip-btn--ghost ip-btn--sm" style={{ marginTop: 14, minWidth: 160 }} onClick={() => sendToParent("Full HPR")}><Icon name="arrow-right" size={15} /> Preview report</button>
          </ProgressCard>

          <ProgressCard>
            <ModHead icon="calendar" title="AI-POWERED EXAM TIMETABLE" />
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 52, lineHeight: 1.6 }}>Automatically generates personalized exam timetables with customized preparation roadmaps based on subjects, schedules, and exam priorities.</div>
          </ProgressCard>

          <ProgressCard>
            <ModHead icon="analytics" title="AI-POWERED EXAM RESULTS ANALYSIS" />
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 52, lineHeight: 1.6 }}>Analyzes exam performance and creates personalized improvement roadmaps for future exams and academic growth.</div>
          </ProgressCard>

          <ProgressCard>
            <ModHead icon="parent" title="AI-POWERED PARENT-TEACHER MEETINGS" />
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 26, lineHeight: 1.6 }}>AI continuously analyzes performance metrics to recommend proactive, data-driven interventions, including early issue detection, meeting frequency suggestions, and generated discussion points for teachers.</div>
          </ProgressCard>
        </div>)}
      </Section>

      {preview ? <MediaPreview preview={preview} onClose={() => setPreview(null)} /> : null}

      {/* Learning Intelligence Engine */}
      <Section eyebrow="AI Learning Intelligence Engine" title="From daily activity to meaning" style={{ paddingTop: 4 }}>
        <div className="ip-card" style={{ padding: "22px 24px", display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 26, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--fg-tertiary)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>It reads</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {["Attendance", "Homework", "Exam schedule", "Behaviour", "Results", "Teacher remarks"].map((t) => (
                <span key={t} className="ip-tag ip-tag--mute" style={{ fontSize: 11.5, padding: "5px 11px" }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ color: "var(--accent)" }}><Icon name="arrow-right" size={26} stroke={2} /></div>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--fg-tertiary)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>It tells you</div>
            <div className="ip-ticks">
              <div style={{ fontSize: 14, color: "var(--fg)", fontWeight: 600, lineHeight: 1.5 }}>"{first} is disciplined and present — protect that, and close one maths concept gap this fortnight."</div>
            </div>
          </div>
        </div>
      </Section>

      {/* Actionable recommendations */}
      <Section eyebrow="Actionable for parents" title="What to do at home, tonight" style={{ paddingTop: 4, paddingBottom: 40 }}>
        <div className="ip-grid ip-grid--3">
          {[
            { t: "15 minutes of fractions", d: "A short, low-pressure practice — three nights this week.", ic: "growth" },
            { t: "Praise the routine, not the marks", d: "Reinforce the 18-day streak to protect motivation.", ic: "sparkles" },
            { t: "Light, not heavy", d: "Maths confidence is fragile right now — keep it playful.", ic: "shield" },
          ].map((r) => (
            <div key={r.t} className="ip-card" style={{ padding: 18, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div className="ip-iconbox" style={{ width: 32, height: 32, borderRadius: 9 }}><Icon name={r.ic} size={16} /></div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--fg)" }}>{r.t}</div>
                <div style={{ fontSize: 12.5, color: "var(--fg-secondary)", marginTop: 4, lineHeight: 1.5 }}>{r.d}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function ModHead({ icon, title, isSent = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div className="ip-iconbox" style={{ width: 30, height: 30, borderRadius: 8 }}><Icon name={icon} size={15} /></div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fg)", letterSpacing: "-0.01em" }}>{title}</span>
      {isSent ? <SentBadge /> : null}
    </div>
  );
}

function ProgressCard({ children, selected = false }) {
  return (
    <div
      className="ip-card"
      style={{
        minHeight: 188,
        padding: 24,
        background: selected ? "color-mix(in srgb, var(--accent) 6%, var(--bg))" : "var(--bg)",
        borderColor: selected ? "color-mix(in srgb, var(--accent) 30%, var(--border))" : "var(--border)",
      }}
    >
      {children}
    </div>
  );
}

function moduleIcon(module = '') {
  const value = module.toUpperCase();
  if (value.includes('ATTENDANCE') || value.includes('TIMETABLE')) return 'calendar';
  if (value.includes('HOMEWORK')) return 'check';
  if (value.includes('PARENT')) return 'parent';
  if (value.includes('RESULT') || value.includes('ANALYSIS')) return 'analytics';
  return 'sparkles';
}

function MediaPreview({ preview, onClose }) {
  return <div className="ip-preview-backdrop" role="dialog" aria-modal="true" aria-label={`${preview.type} preview`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className={`ip-preview-modal ip-preview-modal--${preview.type}`}><div className="ip-preview-modal__head"><div className="ip-preview-modal__identity"><span className="ip-preview-modal__type">{preview.type}</span><strong>{preview.title}</strong><small>Personalised content preview</small></div><button type="button" onClick={onClose} aria-label="Close preview">×</button></div><div className="ip-preview-modal__stage">{preview.type === 'audio' ? <><div className="ip-preview-modal__audio-art"><Icon name="voice" size={30} /></div><audio controls autoPlay src={preview.url} /></> : <video controls autoPlay playsInline src={preview.url} />}</div><div className="ip-preview-modal__foot"><span>Preview only</span><small>Use the player controls to adjust volume or playback.</small></div></div></div>;
}

function CardSkeletonGrid() {
  return <div className="ip-card-skeleton-grid" role="status" aria-label="Loading personalised student insights">{Array.from({ length: 6 }).map((_, index) => <div className="ip-card ip-card-skeleton" key={index}><span className="ip-skeleton ip-card-skeleton__icon" /><span className="ip-skeleton ip-card-skeleton__tag" /><span className="ip-skeleton ip-card-skeleton__line" /><span className="ip-skeleton ip-card-skeleton__line ip-card-skeleton__line--short" /><span className="ip-skeleton ip-card-skeleton__action" /></div>)}</div>;
}




